import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { SessionProvider } from './contexts/SessionContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { MobileMenuProvider, useMobileMenu } from './contexts/MobileMenuContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ActivateAccountPage from './pages/ActivateAccountPage';
import SuperAdminDashboard from './pages/dashboards/SuperAdminDashboard';
import CfaAdminDashboard from './pages/dashboards/CfaAdminDashboard';
import StudentDashboard from './pages/dashboards/StudentDashboard';
import TrainerDashboard from './pages/dashboards/TrainerDashboard';
import StudentsPage from './pages/StudentsPage';
import TrainersPage from './pages/TrainersPage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import AIAssistantPage from './pages/AIAssistantPage';

// Student Pages
import HomeworkPage from './pages/student/HomeworkPage';
import EvaluationsPage from './pages/student/EvaluationsPage';
import DocumentsPage from './pages/student/DocumentsPage';
import LiveClassesPage from './pages/student/LiveClassesPage';
import ResultsPage from './pages/student/ResultsPage';
import ForumPage from './pages/student/ForumPage';
import ProfilePage from './pages/student/ProfilePage';
import SupportPage from './pages/student/SupportPage';
import MessagesPage from './pages/student/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import StudentCoursePage from './pages/student/StudentCoursePage';
import StudentLessonPage from './pages/student/StudentLessonPage';
import StudentQuizPage from './pages/student/StudentQuizPage';
import StudentFundingPage from './pages/student/StudentFundingPage';
import StudentPlanningPage from './pages/student/StudentPlanningPage';
import StudentAttendancePage from './pages/student/StudentAttendancePage';

// Trainer Pages
import TrainerCourseManagement from './pages/trainer/TrainerCourseManagement';
import TrainerStudentProfile from './pages/trainer/TrainerStudentProfile';
import TrainerPlanningPage from './pages/trainer/TrainerPlanningPage';
import TrainerAttendancePage from './pages/trainer/TrainerAttendancePage';
import TrainerProfilePage from './pages/trainer/TrainerProfilePage';
import CreateAssignmentPage from './pages/trainer/CreateAssignmentPage';
import CreateEvaluationPage from './pages/trainer/CreateEvaluationPage';
import ScheduleLivePage from './pages/trainer/ScheduleLivePage';
import SendAnnouncementPage from './pages/trainer/SendAnnouncementPage';
import GradingPage from './pages/trainer/GradingPage';
import TrainerHomeworkSubmissionsPage from './pages/trainer/TrainerHomeworkSubmissionsPage';
import TrainerEvaluationsPage from './pages/trainer/TrainerEvaluationsPage';

// CFA Admin Pages
import CfaAdminPlanningPage from './pages/admin/CfaAdminPlanningPage';
import CfaAdminSettingsPage from './pages/admin/CfaAdminSettingsPage';
import CfaAdminAnnouncementsPage from './pages/admin/CfaAdminAnnouncementsPage';
import CfaAdminDocumentsPage from './pages/admin/CfaAdminDocumentsPage';
import CfaAdminCompaniesPage from './pages/admin/CfaAdminCompaniesPage';
import AlternantsDashboardPage from './pages/admin/AlternantsDashboardPage';
import GroupsManagementPage from './pages/admin/GroupsManagementPage';
import CourseManagementPage from './pages/admin/CourseManagementPage';
import CourseEditorPage from './pages/admin/CourseEditorPage';
import ResourceLibraryPage from './pages/admin/ResourceLibraryPage';
import CourseAnalyticsPage from './pages/admin/CourseAnalyticsPage';
import FinanceModulePage from './pages/admin/FinanceModulePage';
import AttendanceManagementPage from './pages/admin/AttendanceManagementPage';
import LiveSessionsAdminPage from './pages/admin/LiveSessionsAdminPage';
import PedagogicalReportPage from './pages/admin/PedagogicalReportPage';
import BrandingSettingsPage from './pages/admin/BrandingSettingsPage';
import UserInvitationPage from './pages/admin/UserInvitationPage';

// Shared Pages
import TicketSupportPage from './pages/TicketSupportPage';

// Super Admin Pages
import SuperAdminDashboardNew from './pages/super-admin/SuperAdminDashboard';
import CentersManagementPage from './pages/super-admin/CentersManagementPage';
import CfaAdminsPage from './pages/super-admin/CfaAdminsPage';
import PlatformSupportPage from './pages/super-admin/PlatformSupportPage';
import AnalyticsPage from './pages/super-admin/AnalyticsPage';
import SubscriptionsPage from './pages/super-admin/SubscriptionsPage';
import BugReportsPage from './pages/super-admin/BugReportsPage';

import FloatingBugButton from './components/FloatingBugButton';

import './App.css';

const DashboardRouter = ({ onMenuClick }) => {
  const { user } = useAuth();

  if (!user) return null;

  const dashboards = {
    super_admin: SuperAdminDashboardNew,
    cfa_admin: CfaAdminDashboard,
    trainer: TrainerDashboard,
    student: StudentDashboard,
  };

  const DashboardComponent = dashboards[user.role];
  return DashboardComponent
    ? <DashboardComponent onMenuClick={onMenuClick} />
    : <Navigate to="/unauthorized" />;
};

const DocumentsRouter = ({ onMenuClick }) => {
  const { user } = useAuth();

  if (!user) return null;

  // CFA Admin sees admin documents page, students see student documents page
  if (user.role === 'cfa_admin') {
    return <CfaAdminDocumentsPage onMenuClick={onMenuClick} />;
  }
  return <DocumentsPage onMenuClick={onMenuClick} />;
};

const AppLayout = ({ children }) => {
  const location = useLocation();

  return (
    <MobileMenuProvider>
      <AppLayoutInner location={location}>{children}</AppLayoutInner>
    </MobileMenuProvider>
  );
};

const AppLayoutInner = ({ children, location }) => {
  const { toggleMenu } = useMobileMenu();

  // Keep onMenuClick for pages that still read the prop; Header also uses MobileMenuContext.
  const page = React.isValidElement(children)
    ? React.cloneElement(children, { onMenuClick: toggleMenu })
    : children;

  return (
    // Do NOT put overflow-x-hidden on this outer flex: on Android Chrome it
    // breaks position:fixed for the sidebar (menu appears to do nothing).
    <div className="flex min-h-screen min-h-[100dvh]">
      <Sidebar />
      <div className="flex-1 md:ml-64 min-w-0 max-w-full overflow-x-hidden">
        {/* Keyed by route so a crash on one page resets when navigating elsewhere */}
        <ErrorBoundary key={location.pathname}>
          {page}
        </ErrorBoundary>
      </div>
      <FloatingBugButton />
    </div>
  );
};

const PlaceholderPage = ({ title, onMenuClick }) => {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="glass-header sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-2xl md:text-3xl font-bold text-indigo-950 dark:text-white font-outfit">
          {title}
        </h1>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{title}</h2>
          <p className="text-slate-600 dark:text-slate-400">Cette page est en cours de développement</p>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <SessionProvider>
        <WebSocketProvider>
          <BrandingProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/activate" element={<ActivateAccountPage />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <DashboardRouter />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/students"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin', 'trainer']}>
                <AppLayout>
                  <StudentsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainers"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <TrainersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CoursesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/courses/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseDetailPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TrainerPlanningPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Trainer Workspace Routes */}
          <Route
            path="/trainer/courses/:courseId"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <TrainerCourseManagement />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/students/:studentId"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <TrainerStudentProfile />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/planning"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <TrainerPlanningPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/attendance"
            element={
              <ProtectedRoute allowedRoles={['trainer']}>
                <AppLayout>
                  <TrainerAttendancePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/create-assignment"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <CreateAssignmentPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/create-evaluation"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <CreateEvaluationPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/schedule-live"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <ScheduleLivePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/send-announcement"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <SendAnnouncementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/grading"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <GradingPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/homework/:homeworkId/submissions"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <TrainerHomeworkSubmissionsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/evaluations"
            element={
              <ProtectedRoute allowedRoles={['trainer', 'cfa_admin']}>
                <AppLayout>
                  <TrainerEvaluationsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/trainer/profile"
            element={
              <ProtectedRoute allowedRoles={['trainer']}>
                <AppLayout>
                  <TrainerProfilePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/ai-assistant"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <AIAssistantPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/homework"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <HomeworkPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/student/courses/:courseId"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <StudentCoursePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/student/courses/:courseId/lessons/:lessonId"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <StudentLessonPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/student/courses/:courseId/quiz/:quizId"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <StudentQuizPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/student/funding"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <StudentFundingPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/student/planning"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <StudentPlanningPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/student/attendance"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <StudentAttendancePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/evaluations"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <EvaluationsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/documents"
            element={
              <ProtectedRoute allowedRoles={['student', 'cfa_admin']}>
                <AppLayout>
                  <DocumentsRouter />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/live-classes"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <LiveClassesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/results"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <ResultsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/forum"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <ForumPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/forum/:id"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AppLayout>
                  <PlaceholderPage title="Discussion Forum" />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TicketSupportPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <NotificationsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          {/* CFA Admin Routes */}
          <Route
            path="/admin/planning"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <CfaAdminPlanningPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/attendance"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <AttendanceManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <CfaAdminSettingsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/branding"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <BrandingSettingsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/invitations"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <UserInvitationPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <CfaAdminCompaniesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/alternants"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <AlternantsDashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/announcements"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <CfaAdminAnnouncementsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/announcements/new"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <CfaAdminAnnouncementsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/live-sessions"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <LiveSessionsAdminPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/groups"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <GroupsManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/finance"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <FinanceModulePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/pedagogical-report"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin']}>
                <AppLayout>
                  <PedagogicalReportPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/courses"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin', 'trainer']}>
                <AppLayout>
                  <CourseManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/courses/:courseId/edit"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin', 'trainer']}>
                <AppLayout>
                  <CourseEditorPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/resources"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin', 'trainer']}>
                <AppLayout>
                  <ResourceLibraryPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/courses/:courseId/analytics"
            element={
              <ProtectedRoute allowedRoles={['cfa_admin', 'trainer']}>
                <AppLayout>
                  <CourseAnalyticsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Super Admin Routes */}
          <Route
            path="/admin/centers"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <CentersManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/centers/:centerId"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <CentersManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/cfa-admins"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <CfaAdminsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/platform-support"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <PlatformSupportPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/platform-support/:ticketId"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <PlatformSupportPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <AnalyticsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/subscriptions"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <SubscriptionsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/bug-reports"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <BugReportsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/bug-reports/:reportId"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <BugReportsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/platform-settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AppLayout>
                  <PlaceholderPage title="Paramètres Plateforme" />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Legacy routes - redirect to new ones */}
          <Route
            path="/organizations"
            element={<Navigate to="/admin/centers" replace />}
          />
          
          <Route
            path="/analytics"
            element={<Navigate to="/admin/analytics" replace />}
          />
          
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TicketSupportPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ProfilePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/messages"
            element={
              <ProtectedRoute allowedRoles={['student', 'trainer', 'cfa_admin']}>
                <AppLayout>
                  <MessagesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/unauthorized"
            element={
              <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">Accès refusé</h1>
                  <p className="text-slate-600 dark:text-slate-400">Vous n'avez pas les permissions nécessaires</p>
                </div>
              </div>
            }
          />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
          </BrandingProvider>
        </WebSocketProvider>
      </SessionProvider>
    </AuthProvider>
  );
}

export default App;
